<template>
  <div class="desktop-splash">
    <div class="splash-bg-blobs" aria-hidden="true">
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>
      <div class="blob blob-3"></div>
    </div>

    <Transition name="splash-close-fade">
      <CloseButton
        v-if="closeVisible"
        class="splash-close"
        @click="dismiss"
      />
    </Transition>

    <div class="splash-content">
      <span class="splash-chroma" aria-hidden="true"></span>
      <Wordmark class="splash-logo" />
      <h3 class="splash-headline">
        Best on your phone <span class="sparkle" aria-hidden="true">✦</span>
      </h3>
      <p class="splash-copy">
        Scrawl is built for finger drawings — the kind you make in seconds and
        send to a friend. Grab your phone for the real thing.
      </p>

      <div class="qr-row" aria-hidden="true">
        <div class="qr-stub" aria-label="QR code for www.scrawl.ink">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 25" shape-rendering="crispEdges" class="qr-svg">
            <path fill="#ffffff" d="M0 0h25v25H0z"/>
            <path stroke="#1a0a2e" d="M0 0.5h7m4 0h1m1 0h1m2 0h1m1 0h7M0 1.5h1m5 0h1m4 0h1m2 0h3m1 0h1m5 0h1M0 2.5h1m1 0h3m1 0h1m1 0h1m1 0h3m3 0h1m1 0h1m1 0h3m1 0h1M0 3.5h1m1 0h3m1 0h1m1 0h4m1 0h1m1 0h2m1 0h1m1 0h3m1 0h1M0 4.5h1m1 0h3m1 0h1m1 0h1m3 0h2m2 0h1m1 0h1m1 0h3m1 0h1M0 5.5h1m5 0h1m1 0h1m2 0h6m1 0h1m5 0h1M0 6.5h7m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h7M8 7.5h5m2 0h1M0 8.5h1m1 0h5m2 0h1m1 0h5m2 0h5M0 9.5h1m3 0h1m3 0h3m2 0h1m2 0h2m1 0h1m3 0h1M1 10.5h1m1 0h1m2 0h4m1 0h2m2 0h1m1 0h5m1 0h2M0 11.5h1m2 0h1m3 0h1m3 0h1m2 0h1m2 0h1m1 0h1m4 0h1M0 12.5h3m1 0h3m1 0h3m2 0h8m1 0h3M0 13.5h6m4 0h1m1 0h2m2 0h2m1 0h1m1 0h1m1 0h1M0 14.5h1m5 0h3m1 0h1m2 0h9m1 0h2M0 15.5h1m2 0h2m2 0h4m1 0h1m4 0h1m1 0h2m3 0h1M0 16.5h1m1 0h5m1 0h2m2 0h9m1 0h1M8 17.5h1m4 0h1m1 0h2m3 0h2M0 18.5h7m2 0h5m2 0h1m1 0h1m1 0h1m1 0h3M0 19.5h1m5 0h1m1 0h1m1 0h2m2 0h1m1 0h1m3 0h2M0 20.5h1m1 0h3m1 0h1m1 0h4m3 0h6m1 0h3M0 21.5h1m1 0h3m1 0h1m1 0h2m2 0h2m2 0h1m1 0h1m1 0h5M0 22.5h1m1 0h3m1 0h1m1 0h1m1 0h1m2 0h3m5 0h2m1 0h1M0 23.5h1m5 0h1m5 0h2m2 0h2m1 0h3m2 0h1M0 24.5h7m1 0h3m1 0h1m1 0h1m4 0h6"/>
          </svg>
        </div>
        <div class="qr-meta">
          <b>scan to open</b>
          <span>www.scrawl.ink on your phone</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const CLOSE_REVEAL_MS = 5000;

import CloseButton from "./common/CloseButton.vue";
import Wordmark from "./Wordmark.vue";

export default {
  name: "DesktopSplash",
  components: { CloseButton, Wordmark },
  emits: ["dismiss"],
  data() {
    return {
      closeVisible: false,
    };
  },
  mounted() {
    // Mark as seen the moment the splash renders, not on dismiss. The close
    // button is hidden for the first 5s, so users who close the tab / refresh
    // / navigate away early would otherwise see the splash again every visit.
    try {
      localStorage.setItem("scrawl_desktop_splash_seen", "true");
    } catch {
      // Some browsers (private mode, blocked storage) throw — fall through.
    }
    this._closeTimer = setTimeout(() => {
      this.closeVisible = true;
    }, CLOSE_REVEAL_MS);
  },
  beforeUnmount() {
    clearTimeout(this._closeTimer);
  },
  methods: {
    dismiss() {
      this.$emit("dismiss");
    },
  },
};
</script>

<style lang="scss" scoped>
/* ─── Desktop Splash · Soft Y2K Bubble ───
   First-visit overlay shown when a desktop visitor (≥600px viewport,
   pointer:fine) lands on scrawl.fun. The page underneath is fully
   blurred-out by the sky surface; the splash card itself is the hero —
   a glass panel with a chromatic-edge fringe, a holographic gradient
   "✦" sparkle on the headline, ink-soft body copy, and a QR-code stub
   so users can hop to their phone without typing the URL.

   Anchored to mocks/redesign-4-soft-y2k-bubble.html lines 1640–1720
   (.desktop-splash-frame .splash-card).
*/
.desktop-splash {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  place-items: center;
  /* Sky-surface gradient + radial pastel blobs (matches .browser-screen
     in the mock so the splash reads like the in-browser screen behind
     a phone-first product). */
  background:
    radial-gradient(60% 40% at 20% 10%, $sky-2 0%, transparent 60%),
    radial-gradient(70% 50% at 90% 30%, $sky-1 0%, transparent 60%),
    radial-gradient(60% 60% at 30% 90%, $sky-3 0%, transparent 60%),
    linear-gradient(180deg, $surface-1 0%, $surface-2 50%, $surface-3 100%);
  overflow: hidden;
  padding: 32px;
  animation: splash-fade-in 0.35s $ease-soft;
}

@keyframes splash-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* Soft Y2K hero blobs — out-of-focus pastel light through the glass. */
.splash-bg-blobs {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;

  .blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(110px);
    opacity: 0.55;
  }

  .blob-1 {
    width: 520px;
    height: 520px;
    background: $bubblegum;
    top: -160px;
    left: -140px;
  }

  .blob-2 {
    width: 420px;
    height: 420px;
    background: $chrome-blue;
    bottom: -120px;
    right: -110px;
  }

  .blob-3 {
    width: 300px;
    height: 300px;
    background: $mint;
    top: 30%;
    right: 22%;
    opacity: 0.4;
  }
}

/* The glass card itself — `.splash-content` is repurposed as the card
   so we can keep the existing template structure. */
.splash-content {
  position: relative;
  z-index: 1;
  // Sass intercepts the unitless `min()` and rejects vw/px mixing, so
  // express the same constraint with width + max-width — same effect,
  // CSS handles the math instead.
  width: 92vw;
  max-width: 520px;
  padding: 36px 36px 32px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  background: $glass-strong;
  backdrop-filter: $glass-blur;
  -webkit-backdrop-filter: $glass-blur;
  border: 1px solid $glass-edge;
  border-radius: $radius-card;
  box-shadow:
    0 30px 60px -20px rgba($shadow-purple, 0.4),
    0 8px 20px -10px rgba($bubblegum, 0.3),
    inset 0 1px 0 #fff;
  animation: splash-card-in 0.5s $ease-bubble both;
}

/* Holographic halo behind the card — picks up the mock's ::before
   blur(8px) holo glow on .desktop-splash-frame. */
.splash-content::before {
  content: "";
  position: absolute;
  inset: -3px;
  border-radius: calc(#{$radius-card} + 3px);
  background: $holo;
  filter: blur(10px) saturate(120%);
  opacity: 0.55;
  z-index: -1;
  pointer-events: none;
}

/* Chromatic edge — 1px cyan top, 1px magenta bottom, the signature
   "glass catching the light" sparkle. Layered as a decorative span
   so we don't fight with the card's outer ::before halo. */
.splash-chroma {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow:
    inset 0 1px 0 $chrome-blue,
    inset 0 -1px 0 $bubblegum;
  opacity: 0.45;
}

@keyframes splash-card-in {
  from { transform: translateY(8px) scale(0.98); opacity: 0; }
  to   { transform: translateY(0) scale(1); opacity: 1; }
}

.splash-logo {
  --wordmark-size: 56px;
  margin: 0 0 4px;
}

.splash-headline {
  font-family: $font-display;
  font-size: 26px;
  font-weight: 700;
  color: $ink;
  margin: 6px 0 4px;
  letter-spacing: -0.02em;
  line-height: 1.15;

  /* Holographic sparkle ✦ — scoped to the span so the headline copy
     stays high-contrast ink. */
  .sparkle {
    display: inline-block;
    background: $holo-linear;
    background-size: 200% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
    margin-left: 2px;
    animation: sparkle-shift 6s $ease-soft infinite;
  }
}

@keyframes sparkle-shift {
  0%, 100% { background-position: 0% 50%; }
  50%      { background-position: 100% 50%; }
}

.splash-copy {
  font-family: $font-body;
  font-size: 14px;
  font-weight: 400;
  color: $ink-soft;
  line-height: 1.55;
  margin: 0 0 6px;
  max-width: 420px;
}

/* QR row — dashed-divider section under the copy. Icon stub is the
   mock's repeating-line pattern; meta lines explain the scan. */
.qr-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 10px;
  padding-top: 18px;
  width: 100%;
  border-top: 1px dashed rgba($muted-lavender, 0.35);
}

.qr-stub {
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  border-radius: 14px;
  background: #fff;
  border: 2px solid #fff;
  box-shadow: $shadow-chip;
  overflow: hidden;

  .qr-svg {
    display: block;
    width: 100%;
    height: 100%;
  }
}

.qr-meta {
  text-align: left;
  font-family: $font-body;
  font-size: 12px;
  color: $ink-soft;
  font-weight: 600;
  line-height: 1.45;

  b {
    display: block;
    color: $ink;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.01em;
    margin-bottom: 1px;
  }
}

/* Floating dismiss control — chromed glass close button, top-right
   of the viewport (above the card so it doesn't crowd the glass). */
.splash-close {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 2;
}

.splash-close-fade-enter-active {
  transition: opacity 0.4s $ease-soft, transform 0.4s $ease-bubble;
}

.splash-close-fade-enter-from {
  opacity: 0;
  transform: scale(0.85);
}

@media (prefers-reduced-motion: reduce) {
  .desktop-splash,
  .splash-content,
  .splash-headline .sparkle {
    animation: none;
  }
}
</style>
